import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useInView } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Motion from '@/app/components/Motion';

import Integration from './Integration';
import type {
  LandingPageIntegrationData,
  LandingPageIntegrationsLogoColor,
} from './types';

export interface LandingPageIntegrationsProps {
  readonly className?: string;
  readonly description?: MessageKey;
  readonly integrations: LandingPageIntegrationData[];
  readonly logoColor?: LandingPageIntegrationsLogoColor;
  readonly title?: MessageKey;
  readonly inverted?: boolean;
}

const LandingPageIntegrations = ({
  className,
  description,
  integrations,
  logoColor = 'currentColor',
  title,
  inverted = false,
}: LandingPageIntegrationsProps) => {
  const t = useTranslations();
  const hasTitle = Boolean(title);
  const hasDescription = Boolean(description);
  const integrationsGridRef = useRef<HTMLDivElement>(null);
  const isGridInView = useInView(integrationsGridRef, {
    once: true,
  });

  return (
    <LayoutGrid>
      {(hasTitle || hasDescription) && (
        <GridItem
          className="z-10 pb-16"
          colStart={{ sm: 'main', lg: 9 }}
          colEnd={{ sm: 'main', lg: 17 }}
        >
          {hasTitle && (
            <Motion
              initial={{ filter: 'blur(5px)', opacity: 0 }}
              transition={{ duration: 0.75, delay: 0.2 }}
              viewport={{ once: true }}
              whileInView={{ filter: 'blur(0px)', opacity: 1 }}
            >
              <Typography
                align="center"
                className="mb-2 font-normal lg:mb-4"
                Element="h2"
                variant="h4"
                color={inverted ? 'light' : 'inherit'}
              >
                {t(title!)}
              </Typography>
            </Motion>
          )}
          {hasDescription && (
            <Motion
              initial={{ filter: 'blur(5px)', opacity: 0, y: 20 }}
              viewport={{ once: true }}
              transition={{ duration: 0.75, delay: 0.35 }}
              whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
            >
              <Typography
                align="center"
                color={inverted ? 'light' : 'inherit'}
                className="mx-auto max-w-96"
              >
                {t(description!)}
              </Typography>
            </Motion>
          )}
        </GridItem>
      )}
      <GridItem
        colEnd={{ sm: 'main' }}
        colStart={{ sm: 'main' }}
        className="z-10"
      >
        <div
          className="flex flex-wrap justify-start gap-20 md:gap-16 xl:gap-x-24 xxl:gap-x-28"
          ref={integrationsGridRef}
        >
          {integrations.map((integration, i) => (
            <Integration
              className={className}
              index={i}
              inverted={inverted}
              key={integration.name}
              logoColor={logoColor}
              shouldTransition={isGridInView}
              {...integration}
            />
          ))}
        </div>
      </GridItem>
    </LayoutGrid>
  );
};

export default LandingPageIntegrations;
